import { TranslateService } from '@ngx-translate/core';
import { format } from 'date-fns';
import { presetMinutes } from '@sensika/ag-grid-kit';

import { Period } from 'src/app/core/models/core.models';

/** The window a now-relative preset stands for at `nowMs`, in unix seconds. */
export function relativePeriod(minutes: number, nowMs = Date.now()): Period {
  const endTimestamp = Math.floor(nowMs / 1000);
  return { startTimestamp: endTimestamp - minutes * 60, endTimestamp };
}

/**
 * "Last 1 hour" / "Last 15 minutes" / "Last 2 days" for a now-relative
 * duration: days above one when the minutes divide into whole days, hours
 * when they divide into whole hours, minutes otherwise.
 */
export function relativePeriodLabel(
  minutes: number,
  translateService: TranslateService,
): string {
  const [count, unit] =
    minutes >= 2 * 1440 && minutes % 1440 === 0
      ? [minutes / 1440, 'day']
      : minutes % 60 === 0
        ? [minutes / 60, 'hour']
        : [minutes, 'minute'];
  const t = (key: string): string =>
    translateService.instant(`date-range-picker.${key}`);
  return `${t('relative.last')} ${count} ${t(count === 1 ? `unit.${unit}` : `unit.${unit}s`)}`;
}

/**
 * The period as a title suffix: the relative label while a now-relative
 * preset is active, else the absolute range in `dateFormat`.
 */
export function periodLabel(
  period: Period,
  preset: string | null | undefined,
  translateService: TranslateService,
  dateFormat = 'dd MMM HH:mm',
): string {
  const minutes = presetMinutes(preset);
  return minutes === null
    ? `${format(period.startTimestamp * 1000, dateFormat)} - ${format(period.endTimestamp * 1000, dateFormat)}`
    : relativePeriodLabel(minutes, translateService);
}
